import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate, useParams } from 'react-router';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import * as Yup from 'yup';
import { styles } from './styles';
import { utilStyles } from '../../../utils/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import UpdateChangeRequestDetails from './UpdateChangeRequestDetails';
import { getFormInitialValues } from '../../../utils/get-form-initial-values';
import { hasPermission } from '../../../utils/permission';
import minorCategoryIcon from '../../../assets/images/minorCategory.svg';
import majorCategoryIcon from '../../../assets/images/majorCategory.svg';
import PositionStatusChip from '../../../features/positions/Positions/PositionStatusChip';
import DynamicField from '../../../components/common/InputField/DynamicField';
import {
    usePreModifyChangeRequestMutation,
    usePreModifyChangeRequestWithCompaFieldsMutation,
    usePreCreateChangeRequestMutation,
    usePreCreateChangeRequestWithCompaFieldsMutation,
    usePreCreateBulkChangeRequestMutation,
    usePreCreateBulkChangeRequestWithCompaFieldsMutation,
    useCreateChangeRequestMutation,
    useCreateChangeRequestWithCompaFieldsMutation,
    useCreateBulkChangeRequestMutation,
    useCreateBulkChangeRequestWithCompaFieldsMutation,
    useModifyChangeRequestMutation,
    useModifyChangeRequestWithCompaFieldsMutation,
    useDisplayChangeRequestMutation,
    useDisplayChangeRequestWithCompaFieldsMutation,
} from '../../../api/changeRequests';
import { setLoading } from '../../../store/slices/app';
import { allowedChangeRequestActions } from '../ChangeRequestMenu';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '../../../components/common/Dialog/ConfirmationDialog';

const MIN_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const CHANGE_CATEGORY_ICON_SLOT_SX = {
    width: 28,
    minHeight: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mr: 1,
};

const changeCategoryImgSx = {
    width: 20,
    height: 20,
    objectFit: 'contain',
    display: 'block',
};

function ChangeCategorySelectRow({ value }) {
    const icon =
        value === 'Minor' ? (
            <Box component="img" src={minorCategoryIcon} alt="" sx={changeCategoryImgSx} />
        ) : value === 'Major' ? (
            <Box component="img" src={majorCategoryIcon} alt="" sx={changeCategoryImgSx} />
        ) : (
            <AccountBalanceIcon sx={{ fontSize: 20, color: '#ed7342' }} />
        );
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={CHANGE_CATEGORY_ICON_SLOT_SX}>{icon}</Box>
            <Typography component="span" variant="body2" sx={{ lineHeight: 1.5 }}>
                {value}
            </Typography>
        </Box>
    );
}

const ModifyChangeRequest = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();

    const [metaCustomFields, setMetaCustomFields] = useState([]);
    const [openDialog, setOpenDialog] = useState();
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [changeRequest, setChangeRequest] = useState();
    const [preCreateUpdateDetails, setPreCreateUpdateDetails] = useState();
    const [categoryType, setCategoryType] = useState('Minor');
    const { positions, selectedPositions } = useSelector((state) => state.positions);
    const user = useSelector((state) => state.session.user);
    const authorities = user.authorities;
    const userHasCompaBulkCreatePermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_CREATE_CHANGE_REQUEST_BULK',
    ]);
    const userHasCompaCreatePermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_CREATE_CHANGE_REQUEST',
    ]);
    const userHasCompaModifyPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_UPDATE_CHANGE_REQUEST',
    ]);
    const userHasCompaDisplayPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_VIEW_CHANGE_REQUEST',
    ]);

    const [preModifyChangeRequest, { isLoading: isPreModifyLoading }] = usePreModifyChangeRequestMutation();
    const [preModifyChangeRequestWithCompa, { isLoading: isPreModifyWithCompaLoading }] =
        usePreModifyChangeRequestWithCompaFieldsMutation();
    const [preCreateChangeRequest, { isLoading: isPreCreateLoading }] = usePreCreateChangeRequestMutation();
    const [preCreateChangeRequestWithCompaFields, { isLoading: isPreCreateWithCompaLoading }] =
        usePreCreateChangeRequestWithCompaFieldsMutation();
    const [preCreateBulkChangeRequest, { isLoading: isPreCreateBulkLoading }] = usePreCreateBulkChangeRequestMutation();
    const [preCreateBulkChangeRequestWithCompaFields, { isLoading: isPreCreateBulkWithCompaLoading }] =
        usePreCreateBulkChangeRequestWithCompaFieldsMutation();
    const [createChangeRequest, { isLoading: isCreateLoading }] = useCreateChangeRequestMutation();
    const [createChangeRequestWithCompa, { isLoading: isCreateWithCompaLoading }] =
        useCreateChangeRequestWithCompaFieldsMutation();
    const [createBulkChangeRequest, { isLoading: isCreateBulkLoading }] = useCreateBulkChangeRequestMutation();
    const [createBulkChangeRequestWithCompa, { isLoading: isCreateBulkWithCompaLoading }] =
        useCreateBulkChangeRequestWithCompaFieldsMutation();
    const [modifyChangeRequest, { isLoading: isModifyLoading }] = useModifyChangeRequestMutation();
    const [modifyChangeRequestWithCompa, { isLoading: isModifyWithCompaLoading }] =
        useModifyChangeRequestWithCompaFieldsMutation();
    const [displayChangeRequest, { isLoading: isDisplayLoading }] = useDisplayChangeRequestMutation();
    const [displayChangeRequestWithCompa, { isLoading: isDisplayWithCompaLoading }] =
        useDisplayChangeRequestWithCompaFieldsMutation();

    const [newMetaValues, setNewMetaValues] = useState([]);
    useEffect(() => {
        if (changeRequest?.changeRequest?.attributes) {
            let newVal = [];
            changeRequest?.changeRequest?.attributes.map((element) => {
                newVal.push({
                    fieldLabel: element.name,
                    fieldValue: element.newValue,
                });
            });
            setNewMetaValues(newVal);
        }
    }, [changeRequest]);

    let initialValues = getFormInitialValues(metaCustomFields, newMetaValues);
    const [isBulkModify, setIsBulkModify] = useState(false);
    const [isModify, setIsModfiy] = useState(false);
    const [isBulkCreate, setIsBulkCreate] = useState(false);
    const [positionDetails, setPositionDetails] = useState();
    const [positionMetaInfo, setPositionMetaInfo] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const getCRDetails = async () => {
            try {
                if (params.id) {
                    const bodyFormData = new FormData();
                    bodyFormData.append('id', params.id);

                    const response = await (userHasCompaModifyPermisson
                        ? preModifyChangeRequestWithCompa(bodyFormData).unwrap()
                        : preModifyChangeRequest(bodyFormData).unwrap());

                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    if (allowedChangeRequestActions('modify_policy', response?.changeRequest, { user })) {
                        setChangeRequest(response);
                        setMetaCustomFields(response?.extendedDef?.metaCustomFieldDefinitions || []);
                        setPreCreateUpdateDetails({
                            designationsList: response?.designationsList,
                            departmentsList: response?.departmentsList,
                            locationsList: response?.locationsList,
                            availablePositionStatuses: response?.availablePositionStatuses,
                            basicAttributes: response?.basicAttributes,
                        });
                        setCategoryType(response?.changeRequest?.categoryType?.name);
                    } else {
                        navigate('/access-denied');
                    }
                } else {
                    if (!isBulkModify) {
                        const bodyFormData = new FormData();
                        const bulkCreate =
                            !params.positionId && window.location.pathname.toLowerCase().includes('/bulk/create');
                        if (bulkCreate) {
                            bodyFormData.append('parentEntityIds', selectedPositions.join(','));
                        } else {
                            bodyFormData.append('parentEntityId', params.positionId);
                        }

                        const response =
                            userHasCompaCreatePermisson || userHasCompaBulkCreatePermisson
                                ? bulkCreate
                                    ? await preCreateBulkChangeRequestWithCompaFields(bodyFormData).unwrap()
                                    : await preCreateChangeRequestWithCompaFields(bodyFormData).unwrap()
                                : bulkCreate
                                  ? await preCreateBulkChangeRequest(bodyFormData).unwrap()
                                  : await preCreateChangeRequest(bodyFormData).unwrap();

                        if (!response) return;
                        if (response?.status === 'ERROR') throw new Error(response?.message);
                        let positions = [];
                        if (bulkCreate) {
                            positions = Object.keys(response.positions).map((item) => response.positions[item]);
                        }
                        if (
                            allowedChangeRequestActions('create_policy', null, {
                                user,
                                position: bulkCreate ? positions : response.position,
                                type: bulkCreate ? 'bulk' : 'single',
                            })
                        ) {
                            setMetaCustomFields(response?.extendedDef?.metaCustomFieldDefinitions || []);
                            setPreCreateUpdateDetails(response);
                            setPositionDetails(bulkCreate ? positions : response.position);
                        } else navigate('/access-denied');
                    }
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        };
        getCRDetails();
    }, []);

    useEffect(() => {
        if (!params.positionId && window.location.pathname.toLowerCase().includes('/bulk/modify')) {
            setIsBulkModify(true);
        } else if (!params.positionId && window.location.pathname.toLowerCase().includes('/bulk/create')) {
            setIsBulkCreate(true);
        } else if (params.positionId && window.location.pathname.includes('/modify')) {
            setIsModfiy(true);
        }
    }, [params, window.location.pathname]);

    const fetchData = async () => {
        const bodyFormData = new FormData();
        bodyFormData.append('id', params.id);
        try {
            const response = await (userHasCompaDisplayPermisson
                ? displayChangeRequestWithCompa(bodyFormData).unwrap()
                : displayChangeRequest(bodyFormData).unwrap());

            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);

            if (response?.changeRequest?.position) {
                setPositionDetails(response?.changeRequest?.position);
                setPositionMetaInfo(response?.changeRequest?.position?.customObjectInstance?.customFieldValues);
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else
                enqueueSnackbar(error?.message || 'Something went wrong! Position could not be fetched', {
                    variant: 'error',
                });
        }
    };

    useEffect(() => {
        if (params.positionId && isModify) {
            fetchData();
        } else if (!(isModify || isBulkCreate || isBulkModify)) {
            const position = positions?.data.find((item) => item.id == params.positionId);
            setPositionMetaInfo(position?.customObjectInstance?.customFieldValues);
        }
    }, [params.positionId, isBulkModify, positions, isBulkCreate, isModify, params.id]);

    const compaFields =
        userHasCompaCreatePermisson || userHasCompaBulkCreatePermisson || (userHasCompaModifyPermisson && isModify)
            ? {
                  budgetLowerLimit: '',
                  budgetUpperLimit: '',
                  budgetMidLimit: '',
              }
            : {};

    const validationSchema = Yup.object({
        file: Yup.mixed().test('fileSize', 'File size exceeded 5MB', (value) => {
            if (!value) return true;
            return value.size < MIN_FILE_SIZE;
        }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema,
        validateOnChange: true,
        initialValues: {
            positionId: '',
            positionCode: '',
            hiringManager: '',
            recruiter: '',
            yoe: '',
            department: '',
            designation: '',
            location: '',
            status: '',
            file: null,
            fileName: isModify ? changeRequest?.changeRequest?.fileName : null,
            comments: isModify ? changeRequest?.changeRequest?.comments : '',
            ...compaFields,
            ...initialValues,
        },
        onSubmit: async (values, helpers) => {
            try {
                if (!Object.keys(formik.errors).length) {
                    setOpenConfirmationDialog(true);
                }
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    const handleConfirm = async () => {
        setOpenConfirmationDialog(false);
        await createCR();
        formik.resetForm();
    };

    const getCustomFields = useCallback(
        (metaInfo) => {
            const positionMetaData = metaInfo || positionMetaInfo;
            if (!metaCustomFields?.length) return [];
            return metaCustomFields.map((field) => {
                const positionMetaField =
                    positionMetaData &&
                    positionMetaData.find(
                        ({ metaCustomFieldDefinitionID }) =>
                            metaCustomFieldDefinitionID === field.metaCustomFieldDefinitionID,
                    );
                let fieldListOptions = field?.fieldListOptions;
                const currentValue =
                    positionMetaField?.fieldDataType === 'Date'
                        ? !!positionMetaField?.fieldValue
                            ? moment(positionMetaField?.fieldValue).format('DD-MMM-yyyy')
                            : ''
                        : positionMetaField?.fieldValue;

                if (['Single Selection List', 'Multiple Selection List'].includes(positionMetaField?.fieldDataType)) {
                    fieldListOptions = fieldListOptions?.filter((item) => item !== currentValue);
                }
                return {
                    attributeName: field.fieldLabel,
                    category: field.category?.name,
                    metaCustomFieldDefinitionID: field.metaCustomFieldDefinitionID,
                    visibleOn1Demand: field.visibleOn1Demand,
                    currentValue,
                    newValue: (
                        <DynamicField
                            disabled={field.fieldLabel === 'canvasId'}
                            formik={formik}
                            field={{
                                ...field,
                                fieldListOptions,
                            }}
                        />
                    ),
                };
            });
        },
        [positionMetaInfo, metaCustomFields, formik],
    );

    const basicAttributesCategory = useMemo(() => {
        let categoryMapping = {};
        preCreateUpdateDetails?.basicAttributes?.forEach((item) => {
            categoryMapping[item.attributeTitle] = item?.category;
        });

        return categoryMapping;
    }, [preCreateUpdateDetails]);

    const createCR = async () => {
        const positionsCurrentValue = {};

        const getPositionsCurrentValue = (position, key) => {
            if (!isBulkCreate && !isBulkModify) return position[key]?.name || position[key];
            positionDetails.map((item) => {
                const pos = item.position;
                if (!positionsCurrentValue[pos.id]) positionsCurrentValue[pos.id] = {};
                positionsCurrentValue[pos.id][key] = pos[key]?.name || pos[key];
            });
            return null;
        };

        const getPositionsMetaCurrentValue = (metaInfo, field) => {
            if (!isBulkCreate && !isBulkModify) return metaInfo?.['fieldValue'] || null;
            positionDetails.map((item) => {
                const pos = item.position;
                let positionMetaInfo = pos?.customObjectInstance?.customFieldValues;
                const positionMetaField =
                    positionMetaInfo &&
                    positionMetaInfo.find(
                        ({ metaCustomFieldDefinitionID }) =>
                            metaCustomFieldDefinitionID === field.metaCustomFieldDefinitionID,
                    );
                if (!positionsCurrentValue[pos.id]) positionsCurrentValue[pos.id] = {};
                if (positionMetaField?.fieldLabel)
                    positionsCurrentValue[pos.id][positionMetaField.fieldLabel] =
                        positionMetaField['fieldValue'] || null;
                else positionsCurrentValue[pos.id][field.fieldLabel] = null;
            });
            return null;
        };

        const getAttributes = () => {
            const compaAttributes =
                userHasCompaCreatePermisson ||
                userHasCompaBulkCreatePermisson ||
                (userHasCompaModifyPermisson && isModify)
                    ? [
                          {
                              name: 'budgetLowerLimit',
                              displayName: 'Budget Lower Limit',
                              currentValue:
                                  formik?.values?.budgetLowerLimit &&
                                  formik?.values?.budgetLowerLimit !== '' &&
                                  getPositionsCurrentValue(positionDetails, 'budgetLowerLimit'),
                              newValue: formik?.values?.budgetLowerLimit,
                              type: 'Number',
                          },
                          {
                              name: 'budgetMidLimit',
                              displayName: 'Budget Mid Limit',
                              currentValue:
                                  formik?.values?.budgetMidLimit &&
                                  formik?.values?.budgetMidLimit !== '' &&
                                  getPositionsCurrentValue(positionDetails, 'budgetMidLimit'),
                              newValue: formik?.values?.budgetMidLimit,
                              type: 'Number',
                          },
                          {
                              name: 'budgetUpperLimit',
                              displayName: 'Budget Upper Limit',
                              currentValue:
                                  formik?.values?.budgetUpperLimit &&
                                  formik?.values?.budgetUpperLimit !== '' &&
                                  getPositionsCurrentValue(positionDetails, 'budgetUpperLimit'),
                              newValue: formik?.values?.budgetUpperLimit,
                              type: 'Number',
                          },
                      ]
                    : [];

            const attributes = [
                // {
                //   name: 'title',
                //   displayName: 'Title',
                //   currentValue: positionDetails?.title || null,
                //   newValue: formik?.values?.title,
                //   type: 'String',
                // },
                {
                    name: 'status',
                    displayName: 'Status',
                    currentValue:
                        formik?.values?.status &&
                        formik?.values?.status !== '' &&
                        getPositionsCurrentValue(positionDetails, 'status'),
                    newValue: formik?.values?.status,
                    type: 'String',
                },
                {
                    name: 'positionCode',
                    displayName: 'Position code',
                    currentValue:
                        formik?.values?.positionCode &&
                        formik?.values?.positionCode !== '' &&
                        getPositionsCurrentValue(positionDetails, 'positionCode'),
                    newValue: formik?.values?.positionCode,
                    type: 'String',
                },
                {
                    name: 'designation',
                    displayName: 'Designation',
                    currentValue:
                        formik?.values?.designation &&
                        formik?.values?.designation !== '' &&
                        getPositionsCurrentValue(positionDetails, 'designation'),
                    newValue: formik?.values?.designation,
                    type: 'String',
                },
                {
                    name: 'department',
                    displayName: 'Department',
                    currentValue:
                        formik?.values?.department &&
                        formik?.values?.department !== '' &&
                        getPositionsCurrentValue(positionDetails, 'department'),
                    newValue: formik?.values?.department,
                    type: 'String',
                },
                {
                    name: 'location',
                    displayName: 'Location',
                    currentValue:
                        formik?.values?.location &&
                        formik?.values?.location !== '' &&
                        getPositionsCurrentValue(positionDetails, 'location'),
                    newValue: formik?.values?.location,
                    type: 'String',
                },
                ...compaAttributes,
            ];

            const metaFieldMatchesCategory = (field) => {
                if (categoryType === 'Client') {
                    return field.category?.name === 'Major' && field.visibleOn1Demand === true;
                }
                return field.category?.name === categoryType;
            };

            metaCustomFields.filter(metaFieldMatchesCategory).forEach((field) => {
                if (formik?.values[field.fieldLabel]) {
                    const positionMetaField =
                        positionMetaInfo &&
                        positionMetaInfo.find(
                            ({ metaCustomFieldDefinitionID }) =>
                                metaCustomFieldDefinitionID === field.metaCustomFieldDefinitionID,
                        );
                    const newValue =
                        field.fieldDataType === 'Date'
                            ? moment(formik?.values[field.fieldLabel]).format('DD-MMM-yyyy')
                            : formik?.values[field.fieldLabel];
                    attributes.push({
                        name: field.fieldLabel,
                        displayName: field.fieldLabel,
                        currentValue:
                            newValue && newValue !== '' && getPositionsMetaCurrentValue(positionMetaField, field),
                        newValue: newValue,
                        type: field.fieldDataType,
                    });
                }
            });

            return attributes;
        };

        const data = {
            comments: formik?.values?.comments || '',
            ...(formik?.values?.file !== null && { file: formik?.values?.file }),
            ...(isBulkCreate
                ? { parentEntityIds: selectedPositions.join(',') }
                : { parentEntityId: params.positionId }),
            parentClassName: 'Position',
        };

        const bodyFormData = new FormData();

        Object.keys(data).forEach((key) => {
            bodyFormData.append(key, data[key]);
        });

        getAttributes()
            .filter((attribute) => attribute.newValue && attribute.newValue !== '')
            .map((data, index) => {
                Object.keys(data).forEach((key) => {
                    bodyFormData.append(`attributes[${index}].${key}`, data[key]);
                });
            });

        if (isModify) {
            bodyFormData.append('id', params.id);
        }

        bodyFormData.append('category', categoryType);
        if (isBulkCreate || isBulkModify)
            bodyFormData.append('positionsCurrentValue', JSON.stringify(positionsCurrentValue));

        try {
            dispatch(setLoading(true));
            const apiPath = isModify
                ? userHasCompaModifyPermisson
                    ? modifyChangeRequestWithCompa
                    : modifyChangeRequest
                : isBulkCreate
                  ? userHasCompaBulkCreatePermisson
                      ? createBulkChangeRequestWithCompa
                      : createBulkChangeRequest
                  : userHasCompaCreatePermisson
                    ? createChangeRequestWithCompa
                    : createChangeRequest;

            const response = await apiPath(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);

            enqueueSnackbar(`Change request ${isModify ? 'modified' : 'created'} successfully`, {
                variant: 'success',
            });
            navigate('/positions');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleCategoryChange = (e) => {
        formik.resetForm();
        setCategoryType(e.target.value);
    };

    const allowCreate = useMemo(() => {
        return (
            Object.keys(formik.values).filter(
                (key) =>
                    ['comments', 'file', 'fileName'].indexOf(key) < 0 &&
                    formik.values[key] &&
                    formik.values[key] !== '',
            ).length > 0
        );
    }, [formik.values]);

    const isPreCreateModifyLoading = useMemo(() => {
        return (
            isPreModifyLoading ||
            isPreModifyWithCompaLoading ||
            isPreCreateLoading ||
            isPreCreateWithCompaLoading ||
            isPreCreateBulkLoading ||
            isPreCreateBulkWithCompaLoading
        );
    }, [
        isPreModifyLoading,
        isPreModifyWithCompaLoading,
        isPreCreateLoading,
        isPreCreateWithCompaLoading,
        isPreCreateBulkLoading,
        isPreCreateBulkWithCompaLoading,
    ]);

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">
                    {isBulkModify
                        ? 'Bulk Edit Change Requests'
                        : isBulkCreate
                          ? 'Bulk Create Change Requests'
                          : isModify
                            ? 'Update Change Request'
                            : 'Change Request'}
                </Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <form onSubmit={formik.handleSubmit}>
                            <Grid container direction="column" spacing={2}>
                                <Grid
                                    item
                                    sx={{
                                        display: 'flex',
                                        pb: 2,
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Button
                                        startIcon={<ChevronLeftIcon />}
                                        variant="text"
                                        color="secondary"
                                        onClick={() => navigate(-1)}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        sx={utilStyles.mx(2)}
                                        color="primary"
                                        size="large"
                                        startIcon={<CheckIcon fontSize="small" />}
                                        variant="contained"
                                        type="submit"
                                        disabled={
                                            isPreCreateModifyLoading ||
                                            !allowCreate ||
                                            Object.keys(formik.errors).length > 0
                                        }
                                    >
                                        {isModify || isBulkModify ? 'Update' : 'Create'}
                                    </Button>
                                    <ConfirmationDialog
                                        open={openConfirmationDialog}
                                        onCancel={() => setOpenConfirmationDialog(false)}
                                        onConfirm={handleConfirm}
                                        title={`Confirm ${isModify ? 'Modify' : 'Create'} Change Request`}
                                        message={
                                            isBulkModify || isBulkCreate
                                                ? `Do you wish to ${isBulkModify ? 'modify' : 'create'} change request for ${positionDetails?.length} number of positions?`
                                                : `Do you wish to ${isModify ? 'modify' : 'create'} change request for this position?`
                                        }
                                    />
                                </Grid>
                            </Grid>
                            {!(isBulkModify || isBulkCreate) ? (
                                <Grid
                                    container
                                    spacing={2}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Grid item container spacing={2} justifyContent="center">
                                        <Grid item xs={3} display={'grid'}>
                                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                                Position Code:
                                            </Typography>
                                            <Typography sx={styles.text} color="textPrimary" variant="body2">
                                                {positionDetails?.positionCode}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} display={'grid'}>
                                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                                Position Status:
                                            </Typography>
                                            <Box>
                                                <PositionStatusChip label={positionDetails?.status} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={3} display={'grid'}>
                                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                                Change Policy Name:
                                            </Typography>
                                            <Typography sx={styles.text} color="textPrimary" variant="body2">
                                                {positionDetails?.changeRequestPolicy?.name || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} display={'grid'} />
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid
                                    container
                                    spacing={2}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Grid item container>
                                        <Grid item display={'grid'}>
                                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                                Position Code:
                                            </Typography>
                                            <Typography sx={styles.text} mt={2} color="textPrimary" variant="body2">
                                                {positionDetails?.map((item) => (
                                                    <>
                                                        <Chip
                                                            label={item?.position?.positionCode}
                                                            sx={{ backgroundColor: '#d3edff', mr: 2, mb: 2 }}
                                                            props
                                                            onClick={() => setOpenDialog(item?.position?.id)}
                                                        />
                                                        {openDialog === item?.position?.id && (
                                                            <Dialog
                                                                open={openDialog === item?.position?.id}
                                                                onClose={() => setOpenDialog(false)}
                                                                fullWidth
                                                                maxWidth="md"
                                                            >
                                                                <DialogTitle>
                                                                    Request Details
                                                                    <IconButton
                                                                        aria-label="close"
                                                                        onClick={() => setOpenDialog(false)}
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            right: 12,
                                                                            top: 24,
                                                                            color: (theme) => theme.palette.grey[500],
                                                                        }}
                                                                    >
                                                                        <CloseIcon />
                                                                    </IconButton>
                                                                </DialogTitle>
                                                                <DialogContent>
                                                                    <Grid
                                                                        container
                                                                        spacing={2}
                                                                        direction="column"
                                                                        justifyContent="center"
                                                                        alignItems="center"
                                                                    >
                                                                        <Grid
                                                                            item
                                                                            container
                                                                            spacing={2}
                                                                            justifyContent="center"
                                                                        >
                                                                            <Grid item xs={3} display={'grid'}>
                                                                                <Typography
                                                                                    sx={styles.text}
                                                                                    color="textSecondary"
                                                                                    variant="subtitle2"
                                                                                >
                                                                                    Position Code:
                                                                                </Typography>
                                                                                <Typography
                                                                                    sx={styles.text}
                                                                                    color="textPrimary"
                                                                                    variant="body2"
                                                                                >
                                                                                    {item?.position?.positionCode}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={4} display={'grid'}>
                                                                                <Typography
                                                                                    sx={styles.text}
                                                                                    color="textSecondary"
                                                                                    variant="subtitle2"
                                                                                >
                                                                                    Position Status:
                                                                                </Typography>
                                                                                <Box>
                                                                                    <PositionStatusChip
                                                                                        label={item?.position?.status}
                                                                                    />
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={3} display={'grid'}>
                                                                                <Typography
                                                                                    sx={styles.text}
                                                                                    color="textSecondary"
                                                                                    variant="subtitle2"
                                                                                >
                                                                                    Position Start Date:
                                                                                </Typography>
                                                                                <Typography
                                                                                    sx={styles.text}
                                                                                    color="textPrimary"
                                                                                    variant="body2"
                                                                                >
                                                                                    {item?.position?.startDate
                                                                                        ? moment(
                                                                                              item?.position?.startDate,
                                                                                          ).format('MMM DD yyyy')
                                                                                        : '-'}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={2} display={'grid'} />
                                                                        </Grid>
                                                                    </Grid>
                                                                    <UpdateChangeRequestDetails
                                                                        categoryType={categoryType}
                                                                        position={item?.position}
                                                                        formik={formik}
                                                                        metaCustomFields={getCustomFields(
                                                                            item?.position?.customObjectInstance
                                                                                ?.customFieldValues,
                                                                        )}
                                                                        basicAttributesCategory={
                                                                            basicAttributesCategory
                                                                        }
                                                                        isBulkModify={false}
                                                                        isViewPosition={true}
                                                                    />
                                                                </DialogContent>
                                                            </Dialog>
                                                        )}
                                                    </>
                                                ))}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item xs={12} mt={2}>
                                <Box>
                                    <FormControl
                                        sx={{
                                            width: '200px',
                                            mt: '12px',
                                        }}
                                        size="small"
                                        disabled={isModify}
                                    >
                                        <InputLabel required id="demo-simple-select-label">
                                            Change Category
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            label="Change Category"
                                            value={categoryType}
                                            onChange={handleCategoryChange}
                                            renderValue={(selected) => <ChangeCategorySelectRow value={selected} />}
                                        >
                                            <MenuItem value="Minor">
                                                <ChangeCategorySelectRow value="Minor" />
                                            </MenuItem>
                                            <MenuItem value="Major">
                                                <ChangeCategorySelectRow value="Major" />
                                            </MenuItem>
                                            <MenuItem value="Client">
                                                <ChangeCategorySelectRow value="Client" />
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <UpdateChangeRequestDetails
                                    categoryType={categoryType}
                                    formik={formik}
                                    position={positionDetails}
                                    metaCustomFields={getCustomFields()}
                                    basicAttributesCategory={basicAttributesCategory}
                                    isBulkModify={isBulkModify || isBulkCreate}
                                    changeRequest={changeRequest?.changeRequest}
                                    preCreateUpdateDetails={preCreateUpdateDetails}
                                    isPreCreateModifyLoading={isPreCreateModifyLoading}
                                />
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ModifyChangeRequest;
